import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import Cookies from "universal-cookie";

const BaseUrl = process.env.REACT_APP_BASE_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmationPassword, setConfirmationPassword] = useState('');
  const [message, setMessage] = useState < string | null > (null);
  const [isSuccess, setIsSuccess] = useState < boolean | null > (null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const cookies = new Cookies();
  React.useEffect(() => {
    const token = cookies.get("token");
    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const passwordsMatch = useMemo(
    () => password.length > 0 && confirmationPassword.length > 0 && password === confirmationPassword,
    [password, confirmationPassword]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setIsSuccess(null);

    if (!passwordsMatch) {
      setMessage('Passwords do not match.');
      setIsSuccess(false);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BaseUrl}vendor/reset_password`, {
        token,
        email,
        password,
        password_confirmation: confirmationPassword,
      });
      setIsSuccess(true);
      setMessage('Password reset successfully! Redirecting to login…');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setIsSuccess(false);
      setMessage(err?.response?.data?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ background: 'linear-gradient(135deg, #fde68a 0%, #fff7ed 40%, #ffffff 100%)' }}
    >
      <style>
        {`
          .glass {
            backdrop-filter: blur(12px);
            background: rgba(255, 255, 255, 0.7);
            box-shadow: 0 10px 20px rgba(0,0,0,0.07);
            border: 1px solid rgba(255,255,255,0.3);
          }
        `}
      </style>

      <div className="glass rounded-4 p-4 p-md-5 w-100" style={{ maxWidth: 480 }}>
        <h2 className="fs-3 fw-bold text-dark mb-4 text-center">Reset Password</h2>

        {message && (
          <div className={`alert ${isSuccess ? 'alert-success' : 'alert-danger'} text-center`} role="alert">
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="d-grid gap-3">
          <div>
            <label htmlFor="email" className="form-label fw-semibold text-secondary">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              readOnly
              className="form-control form-control-lg bg-light text-muted"
              placeholder="Email"
            />
          </div>

          <div>
            <label htmlFor="password" className="form-label fw-semibold text-secondary">New Password</label>
            <input
              id="password"
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-control form-control-lg"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <div className="form-text">Use at least 8 characters.</div>
          </div>

          <div>
            <label htmlFor="confirm" className="form-label fw-semibold text-secondary">Confirm Password</label>
            <input
              id="confirm"
              type="password"
              placeholder="Confirm Password"
              value={confirmationPassword}
              onChange={(e) => setConfirmationPassword(e.target.value)}
              className={`form-control form-control-lg ${confirmationPassword && !passwordsMatch ? 'is-invalid' : ''}`}
              required
              autoComplete="new-password"
            />
            <div className="invalid-feedback">Passwords must match.</div>
          </div>

          <button
            type="submit"
            className="btn btn-warning btn-lg fw-semibold shadow-sm d-flex align-items-center justify-content-center"
            disabled={loading}
          >
            {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
            {loading ? 'Resetting…' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
